import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import styles from './PositionForm.module.css';

interface Position {
  id: number;
  title: string;
  description?: string;
  department: string;
  scopes: string[];
  tier: number;
  guide?: string;
  requirements?: string;
  commitmentLevel: number;
  isActive: boolean;
  canManageMedicalTickets?: boolean;
  maxHolders?: number | null;
}

interface Props {
  position: Position | null;
  department: string;
  onClose: () => void;
}

const AVAILABLE_SCOPES = [
  'manageMembers',
  'createPost',
  'managePosts',
  'createEvent',
  'viewInfo',
];

const DEPARTMENTS = [
  { id: 'executive', name: 'Executive' },
  { id: 'operations', name: 'Operations' },
  { id: 'security', name: 'Security' },
  { id: 'intelligence', name: 'Intelligence' },
  { id: 'logistics', name: 'Logistics' },
  { id: 'medical', name: 'Medical' },
  { id: 'engineering', name: 'Engineering' },
  { id: 'exploration', name: 'Exploration' },
];

const PositionForm: React.FC<Props> = ({ position, department, onClose }) => {
  const [formData, setFormData] = useState({
    title: position?.title || '',
    description: position?.description || '',
    department: position?.department || department || '',
    tier: position?.tier || 1,
    guide: position?.guide || '',
    requirements: position?.requirements || '',
    commitmentLevel: position?.commitmentLevel?.toString() || '1',
    scopes: position?.scopes || [],
    isActive: position?.isActive ?? true,
    canManageMedicalTickets: position?.canManageMedicalTickets ?? false,
    maxHolders: position?.maxHolders?.toString() || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'tier' ? Number(value) : value,
    }));
  };

  const handleScopeToggle = (scope: string) => {
    setFormData((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter((s) => s !== scope)
        : [...prev.scopes, scope],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        department: formData.department,
        scopes: formData.scopes,
        tier: formData.tier,
        guide: formData.guide,
        requirements: formData.requirements,
        commitmentLevel: typeof formData.commitmentLevel === 'number' ? formData.commitmentLevel : parseInt(formData.commitmentLevel, 10),
        isActive: formData.isActive,
        canManageMedicalTickets: formData.canManageMedicalTickets,
        maxHolders: formData.maxHolders ? parseInt(formData.maxHolders, 10) : null,
      };

      console.log('Submitting payload:', payload);

      if (position) {
        // Update existing position
        await axios.put(`/positions/${position.id}`, payload);
      } else {
        // Create new position
        await axios.post('/positions', payload);
      }

      onClose();
    } catch (err: any) {
      console.error('Failed to save position:', err);
      console.error('Error response:', err.response?.data);
      console.error('Validation errors:', err.response?.data?.errors);
      
      // Format validation errors
      let errorMessage = err.response?.data?.title || 'Failed to save position';
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorDetails = Object.keys(errors)
          .map(key => `${key}: ${errors[key].join(', ')}`)
          .join('; ');
        errorMessage = `${errorMessage}: ${errorDetails}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <h2>{position ? 'Edit Position' : 'Create New Position'}</h2>

      {error && <div className={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div className={styles.section}>
            <h3>Basic Information</h3>

            <div className={styles.field}>
              <label>
                Position Title <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                maxLength={100}
                placeholder="e.g., Fleet Captain"
              />
            </div>

            <div className={styles.field}>
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                maxLength={500}
                placeholder="Brief description of the position"
              />
            </div>

            <div className={styles.field}>
              <label>
                Department <span className={styles.required}>*</span>
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleChange}
                required
              >
                <option value="">Select a department...</option>
                {DEPARTMENTS.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Classification */}
          <div className={styles.section}>
            <h3>Classification</h3>

            <div className={styles.row}>
              <div className={styles.field}>
                <label>Tier <span className={styles.required}>*</span></label>
                <select name="tier" value={formData.tier} onChange={handleChange} required>
                  <option value="1">Tier 1 - Entry Level</option>
                  <option value="2">Tier 2 - Intermediate</option>
                  <option value="3">Tier 3 - Senior / Leadership</option>
                  <option value="4">Tier 4 - Executive / C-Suite</option>
                </select>
              </div>

              <div className={styles.field}>
                <label>Commitment Level <span className={styles.required}>*</span></label>
                <select
                  name="commitmentLevel"
                  value={formData.commitmentLevel}
                  onChange={handleChange}
                  required
                >
                  <option value="1">Casual - Attend when available</option>
                  <option value="2">Regular - 1-2 events/week</option>
                  <option value="3">Dedicated - 3+ events/week</option>
                  <option value="4">Full Time - Daily involvement</option>
                </select>
              </div>

              <div className={styles.field}>
                <label>Max Holders</label>
                <input
                  type="number"
                  name="maxHolders"
                  value={formData.maxHolders}
                  onChange={handleChange}
                  min="0"
                  placeholder="0 = Unlimited"
                />
                <small>Maximum number of people who can hold this position (0 or empty = unlimited)</small>
              </div>
            </div>
          </div>

          {/* Guide & Responsibilities */}
          <div className={styles.section}>
            <h3>Position Guide & Responsibilities</h3>
            <div className={styles.field}>
              <label>Guide</label>
              <textarea
                name="guide"
                value={formData.guide}
                onChange={handleChange}
                rows={6}
                placeholder="Detailed description of responsibilities, duties, and expectations for this position..."
              />
              <small>This will be shown to members when they view the position details.</small>
            </div>
          </div>

          {/* Requirements */}
          <div className={styles.section}>
            <h3>Requirements</h3>
            <div className={styles.field}>
              <label>Requirements</label>
              <textarea
                name="requirements"
                value={formData.requirements}
                onChange={handleChange}
                rows={4}
                placeholder="e.g., Minimum 2 events per week, Combat certification required, Must complete training program"
              />
              <small>List any specific requirements, certifications, or prerequisites.</small>
            </div>
          </div>

          {/* Permissions/Scopes */}
          <div className={styles.section}>
            <h3>Permissions</h3>
            <div className={styles.scopesGrid}>
              {AVAILABLE_SCOPES.map((scope) => (
                <label key={scope} className={styles.scopeCheckbox}>
                  <input
                    type="checkbox"
                    checked={formData.scopes.includes(scope)}
                    onChange={() => handleScopeToggle(scope)}
                  />
                  <span>{scope}</span>
                </label>
              ))}
            </div>
            
            <div className={styles.field} style={{ marginTop: '1rem' }}>
              <label className={styles.scopeCheckbox}>
                <input
                  type="checkbox"
                  checked={formData.canManageMedicalTickets}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, canManageMedicalTickets: e.target.checked }))
                  }
                />
                <span><strong>Can Manage Medical Tickets</strong></span>
              </label>
              <small style={{ display: 'block', marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                Members with this position can view, claim, and manage medical SOS tickets.
              </small>
            </div>
          </div>

          {/* Status */}
          {position && (
            <div className={styles.section}>
              <h3>Status</h3>
              <label className={styles.scopeCheckbox}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                />
                <span>Position is Active</span>
              </label>
              <small>Inactive positions are archived and won't appear in dropdowns.</small>
            </div>
          )}

          {/* Actions */}
          <div className={styles.actions}>
            <button type="button" onClick={onClose} className={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" disabled={loading} className={styles.submitButton}>
              {loading ? 'Saving...' : position ? 'Update Position' : 'Create Position'}
            </button>
          </div>
        </form>
    </div>
  );
};

export default PositionForm;